import { PROJECT_CONFIG } from './project_branding/config';

// --- Constantes GSM-7 ---

export const GSM_7BIT_BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\x1BÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

export const GSM_7BIT_EXTENDED = '^{}\\[~]|€';

/**
 * Mention de désinscription — obligatoire uniquement sur les SMS **marketing**
 * (campagnes de prospection avec expéditeur personnalisé). Les SMS de service /
 * transactionnels (rappel de RDV, confirmation) en sont dispensés : ils n'ont pas
 * de finalité commerciale. Ne pas l'ajouter aux notifications.
 */
export const MANDATORY_STOP_MSG = ' STOP au 36180';

export const SHORT_URL_LENGTH = 22; // AllMySMS short URLs: https://bs.ms/XXXXXXX

export const SMS_LIMITS = {
  GSM_7: { single: 160, multi: 153, max_segments: 8 },
  UCS_2: { single: 70, multi: 67, max_segments: 8 },
} as const;

// --- Types ---

export enum SmsEncoding {
  GSM_7 = 'GSM-7',
  UCS_2 = 'UCS-2',
}

export interface SmsCharInfo {
  encoding: SmsEncoding;
  char_count: number;
  sms_count: number;
  chars_per_segment: number;
  chars_remaining_in_segment: number;
  max_chars: number;
}

export interface NonGsmChar {
  char: string;
  index: number;
  suggestion: string | null;
  auto_replaceable: boolean;
}

// --- Table de remplacement intelligente ---

export const SMART_REPLACEMENTS: Record<string, string> = {
  '\u201C': '"', // " → "
  '\u201D': '"', // " → "
  '\u2018': "'", // ' → '
  '\u2019': "'", // ' → '
  '\u2014': '-', // — → -
  '\u2013': '-', // – → -
  '\u2026': '...', // … → ...
  '\u00AB': '"', // « → "
  '\u00BB': '"', // » → "
  '\u2039': '<', // ‹ → <
  '\u203A': '>', // › → >
  '\u2022': '-', // • → -
  '\u00A0': ' ', // espace insécable → espace
  // Remise d'une promotion (`formatPromotionValueLabel`, variable `promo:value`) : « −20 % ».
  '\u2212': '-', // − (signe moins) → -
  '\u202F': ' ', // espace fine insécable (format français des nombres) → espace
};

// --- URL detection ---

const URL_REGEX = /https?:\/\/(?:www\.)?[^\s/$.?#].[^\s]*/gi;

export function detectSmsUrls(text: string, limit?: number): string[] {
  const matches = text.match(URL_REGEX);
  if (!matches) return [];
  if (typeof limit === 'number' && limit >= 0) {
    return matches.slice(0, limit);
  }
  return matches;
}

// --- Liens raccourcis (suivi des clics, `track_links`) ---

/** Variable de campagne (clé d'`EMAIL_BINDABLE_FIELDS`) portant le code promo personnel du destinataire. */
export const PERSONAL_PROMO_CODE_BINDING_KEY = 'customer:promo_code';
/** Cette variable telle qu'elle s'écrit dans le texte d'une campagne email ou SMS. */
export const PERSONAL_PROMO_CODE_TOKEN = `{{${PERSONAL_PROMO_CODE_BINDING_KEY}}}`;

/**
 * Liens d'un SMS à raccourcir — règle UNIQUE, partagée par l'estimation (`analyzeSms`,
 * `track_links`) et l'envoi d'une campagne : tout lien est raccourci, SAUF un lien qui cite le code
 * promo personnel (`PERSONAL_PROMO_CODE_TOKEN`).
 *
 * Un lien court est commun à tous les destinataires du lot : raccourci jeton compris,
 * `…/book/slug?promo={{customer:promo_code}}` enverrait chacun vers le même code littéral, invalide
 * (le jeton disparaît du texte avant qu'AllMySMS n'y substitue le code de chacun). Il part long.
 *
 * Rend les liens DISTINCTS, dans l'ordre du texte ; `limit` borne leur nombre (une campagne en
 * raccourcit `LIMIT_SMS_URL`), un lien promo n'y prend pas la place d'un autre.
 */
export function smsUrlsToShorten(text: string, limit?: number): string[] {
  const urls = [...new Set(detectSmsUrls(text))].filter((url): boolean => !url.includes(PERSONAL_PROMO_CODE_TOKEN));
  return typeof limit === 'number' && limit >= 0 ? urls.slice(0, limit) : urls;
}

/**
 * Remplace chaque lien de `replacementByUrl` (lien → remplaçant), en UNE passe et à sa position
 * exacte dans le texte. Pas de `replaceAll` lien par lien : un lien préfixe d'un autre
 * (`…/book/slug` et `…/book/slug?promo={{customer:promo_code}}`) était remplacé aussi au début du
 * lien long, qu'il cassait.
 */
export function replaceSmsUrls(text: string, replacementByUrl: ReadonlyMap<string, string>): string {
  if (replacementByUrl.size === 0) return text;
  let result = '';
  let cursor = 0;
  for (const match of text.matchAll(URL_REGEX)) {
    const replacement = replacementByUrl.get(match[0]);
    if (replacement === undefined) continue;
    const start = match.index ?? 0;
    result += text.slice(cursor, start) + replacement;
    cursor = start + match[0].length;
  }
  return result + text.slice(cursor);
}

/**
 * Le texte d'un SMS tel qu'il partira une fois ses liens raccourcis (`smsUrlsToShorten`) : chaque
 * lien raccourci remplacé par un lien court factice de la longueur d'un lien court AllMySMS, le
 * lien porteur du code promo personnel gardé long. Seule sa LONGUEUR a un sens (estimation du coût).
 */
export function smsTextWithShortenedUrls(
  text: string,
  options?: { limit?: number; short_url_length?: number },
): string {
  const placeholder = 'x'.repeat(options?.short_url_length ?? SHORT_URL_LENGTH);
  return replaceSmsUrls(
    text,
    new Map(smsUrlsToShorten(text, options?.limit).map((url): [string, string] => [url, placeholder])),
  );
}

// --- Emojis ---
//
// AllMySMS supprime TOUS les emojis à l'envoi (testé le 23/10/2026 sur 🍂 ✨ 🤍, `coding: 2`, opérateur
// Orange, avec et sans expéditeur — son interface l'annonce : « émoticônes non supportés… automatiquement
// supprimés »). Les pictogrammes du plan de base (☎ ✔ ♥ ▶ ⚠ ➡) sont refusés de la même façon (vérifié
// le 23/10/2026 dans son interface), d'où `Extended_Pictographic` et pas seulement les emojis astraux.
// On les retire donc NOUS-MÊMES avant tout calcul : un emoji passait le message en UCS-2 (70 caractères
// par SMS au lieu de 160) et le pro payait des segments pour un caractère jamais reçu.
// Sont retirés les pictogrammes et ce qui les compose (sélecteur de variante, ton de peau, liaison ZWJ,
// drapeaux, keycap) ; © ® ™ restent, ce sont des signes typographiques.
const SMS_EMOJI_UNIT =
  '(?:\\u200D)?(?:(?![\\u00A9\\u00AE\\u2122])\\p{Extended_Pictographic}|\\p{Emoji_Modifier}|\\p{Regional_Indicator}|[\\uFE0F\\u20E3]|[\\u{E0020}-\\u{E007F}])';

/** Un emoji (ou un de ses composants), pour les repérer un à un dans un texte. */
const SMS_EMOJI_REGEX = new RegExp(SMS_EMOJI_UNIT, 'gu');

/** Une suite d'emojis avec les espaces qui l'entourent, pour la retirer sans laisser d'espace double. */
const SMS_EMOJI_RUN_REGEX = new RegExp(`([ \\t]*)(?:${SMS_EMOJI_UNIT})+([ \\t]*)`, 'gu');

export interface SmsEmoji {
  char: string;
  /** Position en unités UTF-16 (comme `NonGsmChar.index`). */
  index: number;
}

/** Les emojis d'un texte, qu'AllMySMS supprimera à l'envoi. */
export function findSmsEmojis(text: string): SmsEmoji[] {
  return [...text.matchAll(SMS_EMOJI_REGEX)].map((m): SmsEmoji => ({ char: m[0], index: m.index ?? 0 }));
}

// Autour d'une suite d'emojis retirée, les espaces capturées ne sont pas remises quand elles
// colleraient à une ponctuation qui ne prend pas d'espace avant (« Super 🔥, viens » → « Super, viens »)
// ou à une parenthèse ouvrante (« (🎉 Promo) » → « (Promo) »). Les signes doubles « ! ? ; : » et les
// guillemets « » gardent leur espace, comme le veut la typographie française.
const NO_SPACE_BEFORE_REGEX = /[,.…)\]}]/;
const NO_SPACE_AFTER_REGEX = /[([{]/;

/**
 * Le texte tel qu'il sera reçu : sans ses emojis. Une suite d'emojis entre deux mots laisse une
 * seule espace ; en début ou fin de ligne, ou contre une ponctuation, elle part avec ses espaces.
 */
export function stripSmsEmojis(text: string): string {
  return text.replace(
    SMS_EMOJI_RUN_REGEX,
    (match: string, before: string, after: string, offset: number, whole: string): string => {
      const previous = offset === 0 ? '\n' : whole[offset - 1];
      const end = offset + match.length;
      const next = end === whole.length ? '\n' : whole[end];
      const atLineStart = previous === '\n';
      const atLineEnd = next === '\n' || next === '\r';
      if (atLineStart || atLineEnd) return '';
      if (NO_SPACE_BEFORE_REGEX.test(next) || NO_SPACE_AFTER_REGEX.test(previous)) return '';
      return before || after ? ' ' : '';
    },
  );
}

// --- Fonctions ---

export function classifyChar(char: string): 'basic' | 'extended' | 'unicode' {
  if (GSM_7BIT_BASIC.includes(char)) return 'basic';
  if (GSM_7BIT_EXTENDED.includes(char)) return 'extended';
  return 'unicode';
}

export function analyzeSms(
  message: string,
  options?: {
    include_stop?: boolean;
    track_links?: boolean;
    short_url_length?: number;
  },
): SmsCharInfo {
  const includeStop = options?.include_stop ?? true;
  const trackLinks = options?.track_links ?? false;
  const shortUrlLen = options?.short_url_length ?? SHORT_URL_LENGTH;

  // Compté tel que reçu : sans ses emojis, supprimés à l'envoi (`stripSmsEmojis`). Liens comptés comme
  // l'envoi les enverra (`smsUrlsToShorten`) : raccourcis, sauf le lien porteur du code promo
  // personnel, qui part long.
  const received = stripSmsEmojis(message);
  let effectiveMessage = trackLinks ? smsTextWithShortenedUrls(received, { short_url_length: shortUrlLen }) : received;

  // Append STOP message for counting
  if (includeStop) {
    effectiveMessage = effectiveMessage + MANDATORY_STOP_MSG;
  }

  // Detect encoding and count characters
  let isGSM7 = true;
  let charCount = 0;

  for (const char of effectiveMessage) {
    if (GSM_7BIT_BASIC.includes(char)) {
      charCount += 1;
    } else if (GSM_7BIT_EXTENDED.includes(char)) {
      charCount += 2;
    } else {
      isGSM7 = false;
      break;
    }
  }

  if (isGSM7) {
    const limits = SMS_LIMITS.GSM_7;
    const smsCount = charCount <= limits.single ? 1 : Math.ceil(charCount / limits.multi);
    const charsPerSegment = charCount <= limits.single ? limits.single : limits.multi;
    const maxChars = limits.max_segments * limits.multi;
    const usedInCurrentSegment = charCount <= limits.single ? charCount : charCount % limits.multi || limits.multi;
    const remaining = charsPerSegment - usedInCurrentSegment;

    return {
      encoding: SmsEncoding.GSM_7,
      char_count: charCount,
      sms_count: smsCount,
      chars_per_segment: charsPerSegment,
      chars_remaining_in_segment: remaining,
      max_chars: maxChars,
    };
  }

  // UCS-2: count by string length (UTF-16 code units)
  const ucsLength = effectiveMessage.length;
  const limits = SMS_LIMITS.UCS_2;
  const smsCount = ucsLength <= limits.single ? 1 : Math.ceil(ucsLength / limits.multi);
  const charsPerSegment = ucsLength <= limits.single ? limits.single : limits.multi;
  const maxChars = limits.max_segments * limits.multi;
  const usedInCurrentSegment = ucsLength <= limits.single ? ucsLength : ucsLength % limits.multi || limits.multi;
  const remaining = charsPerSegment - usedInCurrentSegment;

  return {
    encoding: SmsEncoding.UCS_2,
    char_count: ucsLength,
    sms_count: smsCount,
    chars_per_segment: charsPerSegment,
    chars_remaining_in_segment: remaining,
    max_chars: maxChars,
  };
}

/**
 * Caractères hors GSM-7, emojis exclus (supprimés à l'envoi, cf. `findSmsEmojis`). `emojis` : ceux de
 * `message`, à passer quand l'appelant les a déjà calculés (plugin d'édition) pour ne pas les rechercher.
 */
export function findNonGsmChars(message: string, emojis: SmsEmoji[] = findSmsEmojis(message)): NonGsmChar[] {
  const result: NonGsmChar[] = [];
  // Positions UTF-16 couvertes par un emoji (une liaison ZWJ en tête décale le début du match).
  const emojiIndexes = new Set(
    emojis.flatMap((e): number[] =>
      Array.from({ length: e.char.length }, (_unused: unknown, i: number): number => e.index + i),
    ),
  );
  let index = 0;

  for (const char of message) {
    const classification = classifyChar(char);
    if (classification === 'unicode' && !emojiIndexes.has(index)) {
      const suggestion = SMART_REPLACEMENTS[char] ?? null;
      result.push({
        char,
        index,
        suggestion,
        auto_replaceable: suggestion !== null,
      });
    }
    index += char.length; // handle surrogate pairs
  }

  return result;
}

/** Remove diacritics (é → e, ç → c, â → a…) so the text can fit in GSM-7. */
export function stripAccents(message: string): string {
  return message.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Returns true when the only reason the message is UCS-2 is French accents / diacritics
 * that can be safely stripped to get back to GSM-7 (no emoji, no CJK, etc.).
 */
export function canDowngradeToGsm7ViaAccentStrip(message: string): boolean {
  const received = stripSmsEmojis(message);
  const withoutAccents = stripAccents(received);
  for (const char of withoutAccents) {
    if (classifyChar(char) === 'unicode') return false;
  }
  return withoutAccents !== received;
}

export function autoReplaceSmsChars(message: string): { text: string; replacements_count: number } {
  let text = message;
  let count = 0;

  for (const [from, to] of Object.entries(SMART_REPLACEMENTS)) {
    const before = text;
    text = text.split(from).join(to);
    if (before !== text) {
      let occurrences = 0;
      let pos = -1;
      while ((pos = before.indexOf(from, pos + 1)) !== -1) {
        occurrences++;
      }
      count += occurrences;
    }
  }

  return { text, replacements_count: count };
}

function sanitizeSmsSender(value: string): string {
  return stripAccents(value)
    .replace(/[^A-Za-z0-9]/g, '')
    .replace(/^[0-9]+/, '')
    .substring(0, 11);
}

export function deriveSenderName(proName: string): string {
  // SMS sender (alpha) doit être ASCII alphanum ≤11 chars (contrainte AllMySMS).
  // Le fallback brand_string passe par le même nettoyage pour garantir la conformité
  // si la marque contient un accent (ex. "Cfixé" → "Cfixe").
  // Un résultat < 3 chars ne passe pas la validation AllMySMS → fallback aussi.
  const fromName = sanitizeSmsSender(proName);
  if (fromName.length >= 3) return fromName;
  const fromBrand = sanitizeSmsSender(PROJECT_CONFIG.brand_string);
  return fromBrand.length >= 3 ? fromBrand : 'SMS';
}

// --- Optimisation d'un SMS transactionnel ---

export interface OptimizedSms {
  text: string;
  /** true quand les accents ont été retirés pour repasser en GSM-7. */
  accents_stripped: boolean;
}

/**
 * Réduit le coût d'un SMS sans en changer le sens.
 *
 * Un seul caractère hors GSM-7 — le `û` de « août », mais aussi ê â î ô — bascule
 * TOUT le message en UCS-2 : 70 caractères par segment au lieu de 160. Un rappel
 * de RDV banal passe ainsi à 2 SMS facturés.
 *
 * On applique donc les remplacements typographiques (toujours sûrs), puis on
 * retire les accents **uniquement si cela fait réellement gagner un segment** :
 * inutile de dégrader « médecines » en « medecines » sur un message court qui
 * tient de toute façon en un seul SMS.
 */
export function optimizeSmsBody(message: string, options?: { include_stop?: boolean }): OptimizedSms {
  const includeStop = options?.include_stop ?? false;
  // Emojis retirés d'abord : supprimés à l'envoi par AllMySMS (`stripSmsEmojis`), ils ne doivent
  // peser ni dans le choix de l'encodage ni dans le texte journalisé.
  const { text } = autoReplaceSmsChars(stripSmsEmojis(message));

  if (!canDowngradeToGsm7ViaAccentStrip(text)) return { text, accents_stripped: false };

  const stripped = stripAccents(text);
  const before = analyzeSms(text, { include_stop: includeStop }).sms_count;
  const after = analyzeSms(stripped, { include_stop: includeStop }).sms_count;

  return after < before ? { text: stripped, accents_stripped: true } : { text, accents_stripped: false };
}

// --- Résolution de l'expéditeur SMS ---
//
// Politique produit — deux expéditeurs distincts :
// - SMS **marketing** (campagnes) : `professional.sms_marketing_sender_name`,
//   personnalisable par tous les pros ; à défaut, le nom d'établissement normalisé.
//   Jamais la marque de la plateforme.
// - SMS **transactionnels** (rappels, notifications) : la marque de la plateforme,
//   sauf marque blanche où `professional.sms_sender_name` s'applique.

export function resolveCampaignSmsSenderName(
  pro: {
    name?: string | null;
    sms_marketing_sender_name?: string | null;
  },
  campaign_sender?: string | null,
): string {
  // Les campagnes créées avant la dissociation des expéditeurs stockent la marque
  // plateforme (ancien préremplissage depuis `sms_sender_name`, défaut DB) : on la
  // traite comme « non renseigné » — un SMS marketing ne part jamais au nom de la marque.
  if (campaign_sender && campaign_sender !== PROJECT_CONFIG.brand_string) return campaign_sender;
  return pro.sms_marketing_sender_name || deriveSenderName(pro.name ?? '');
}

export function resolveNotificationSmsSenderName(
  pro: { name?: string | null; sms_sender_name?: string | null },
  is_white_label: boolean,
): string {
  if (!is_white_label) return deriveSenderName(PROJECT_CONFIG.brand_string);
  // La valeur par défaut en DB est la marque : on ne peut pas distinguer « jamais
  // personnalisé » de « veut la marque », donc la valeur marque == non personnalisé.
  const custom =
    pro.sms_sender_name && pro.sms_sender_name !== PROJECT_CONFIG.brand_string ? pro.sms_sender_name : null;
  return custom ?? deriveSenderName(pro.name ?? '');
}

// --- Fenêtre horaire SMS marketing ---
//
// Règles déontologiques françaises (CPA/af2m, recommandations CNIL) : pas de SMS
// commercial entre 20h et 8h ni le dimanche (ni les jours fériés — non calculés ici).
// Utilisé pour avertir le pro à la planification, sans bloquer l'envoi.

export const SMS_MARKETING_ALLOWED_HOURS = { start_hour: 8, end_hour: 20 } as const;
export const SMS_MARKETING_DEFAULT_SEND_TIME = '10:00';

/**
 * Valeur LOCALE uniquement : la chaîne attendue est celle du date picker
 * (`YYYY-MM-DD[THH:mm:ss]`, sans timezone). Ne pas passer d'ISO UTC (`...Z`)
 * venant du backend — il serait interprété dans le fuseau du navigateur.
 */
export function isOutsideSmsMarketingHours(value: string | Date): boolean {
  // Interprétation naïve en heure locale : le date picker émet `YYYY-MM-DD[THH:mm:ss]`
  // sans timezone. Une date seule (pas d'heure) vaut minuit → hors fenêtre.
  const date =
    typeof value === 'string' ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value) : value;
  if (!(date instanceof Date) || isNaN(date.getTime())) return false;
  const isSunday = date.getDay() === 0;
  return (
    isSunday ||
    date.getHours() < SMS_MARKETING_ALLOWED_HOURS.start_hour ||
    date.getHours() >= SMS_MARKETING_ALLOWED_HOURS.end_hour
  );
}
